import { Suspense, lazy } from "react";
import { Route, Routes, Navigate } from "react-router";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import Layout from "./components/Layout";
import RequireAdmin from "./auth/RequireAdmin";
import RequireAccounting from "./auth/RequireAccounting";

const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Orders = lazy(() => import("./features/documents/pages/Orders"));
const OrderDetail = lazy(() => import("./features/documents/pages/OrderDetail"));
const Invoices = lazy(() => import("./features/documents/pages/Invoices"));
const InvoiceDetail = lazy(() => import("./features/documents/pages/InvoiceDetail"));
const Shipments = lazy(() => import("./features/documents/pages/Shipments"));
const ShipmentDetail = lazy(() => import("./features/documents/pages/ShipmentDetail"));
const Reports = lazy(() => import("./features/reports/pages/ReportsPage"));
const MarginReport = lazy(() => import("./features/reports/pages/MarginReport"));
const BrandMarginReport = lazy(() => import("./features/reports/pages/BrandMarginReport"));
const LoginsPage = lazy(() => import("./pages/Logins"));

// Intermediation module
const IntermediationLayout = lazy(() => import("./features/intermediation/pages/IntermediationLayout"));
const OrdersListPage = lazy(() => import("./features/intermediation/pages/OrdersListPage"));
const OrderCreatePage = lazy(() => import("./features/intermediation/pages/OrderCreatePage"));
const OrderEditPage = lazy(() => import("./features/intermediation/pages/OrderEditPage"));
const OrderDetailPage = lazy(() => import("./features/intermediation/pages/OrderDetailPage"));
const SuppliersPage = lazy(() => import("./features/intermediation/pages/SuppliersPage"));
const ProductsPage = lazy(() => import("./features/intermediation/pages/ProductsPage"));
const PriceListPage = lazy(() => import("./features/intermediation/pages/PriceListPage"));
const CommissionsPage = lazy(() => import("./features/intermediation/pages/CommissionsPage"));
const DiscountsPage = lazy(() => import("./features/intermediation/pages/DiscountsPage"));
const CustomersPage = lazy(() => import("./features/intermediation/pages/CustomersPage"));
const SupplierInvoicesPage = lazy(() => import("./features/intermediation/pages/SupplierInvoicesPage"));
const CommissionsReportPage = lazy(() => import("./features/intermediation/pages/CommissionsReportPage"));

// Accounting module
const AccountingLayout = lazy(() => import("./features/accounting/pages/AccountingLayout"));
const TrialBalancePage = lazy(() => import("./features/accounting/pages/TrialBalancePage"));
const IncomeStatementPage = lazy(() => import("./features/accounting/pages/IncomeStatementPage"));
const BalanceSheetPage = lazy(() => import("./features/accounting/pages/BalanceSheetPage"));

export default function App() {
  return (
    <Suspense fallback={<div className="p-6 text-sm text-slate-600">Cargando…</div>}>
      <UnauthenticatedTemplate>
        <Routes>
          <Route path="*" element={<Login />} />
        </Routes>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <Layout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/pedidos" element={<Orders />} />
            <Route path="/pedidos/:id" element={<OrderDetail />} />
            <Route path="/facturas" element={<Invoices />} />
            <Route path="/facturas/:id" element={<InvoiceDetail />} />
            <Route path="/albaranes" element={<Shipments />} />
            <Route path="/albaranes/:id" element={<ShipmentDetail />} />
            <Route path="/informes" element={<Reports />} />
            <Route path="/informes/margen" element={<MarginReport />} />
            <Route path="/informes/margen-marca" element={<BrandMarginReport />} />
            <Route path="/admin/logins" element={<RequireAdmin><LoginsPage /></RequireAdmin>} />

            {/* Intermediation module: admin-only (email allowlist). Both the layout route and
                the full-width create/edit/detail routes are wrapped so every intermediation path
                redirects non-admins to the dashboard. */}
            <Route element={<RequireAdmin><IntermediationLayout /></RequireAdmin>}>
              <Route path="/intermediacion" element={<OrdersListPage />} />
              <Route path="/intermediacion/proveedores" element={<SuppliersPage />} />
              <Route path="/intermediacion/clientes" element={<CustomersPage />} />
              <Route path="/intermediacion/catalogo/productos" element={<ProductsPage />} />
              <Route path="/intermediacion/catalogo/precios" element={<PriceListPage />} />
              <Route path="/intermediacion/catalogo/comisiones" element={<CommissionsPage />} />
              <Route path="/intermediacion/catalogo/descuentos" element={<DiscountsPage />} />
              <Route path="/intermediacion/facturas-proveedor" element={<SupplierInvoicesPage />} />
              <Route path="/intermediacion/informes/comisiones" element={<CommissionsReportPage />} />
            </Route>

            {/* Accounting module: own email allowlist (ACCOUNTING_EMAILS ∪ admins). The first
                tab mounts at the base path (repo pattern — no redirect), NavLink uses `end`. */}
            <Route element={<RequireAccounting><AccountingLayout /></RequireAccounting>}>
              <Route path="/contabilidad" element={<TrialBalancePage />} />
              <Route path="/contabilidad/perdidas-y-ganancias" element={<IncomeStatementPage />} />
              <Route path="/contabilidad/balance" element={<BalanceSheetPage />} />
            </Route>

            {/* Order create/edit/detail rendered full-width (outside the tabbed layout) */}
            <Route path="/intermediacion/nuevo" element={<RequireAdmin><OrderCreatePage /></RequireAdmin>} />
            <Route path="/intermediacion/:id" element={<RequireAdmin><OrderDetailPage /></RequireAdmin>} />
            <Route path="/intermediacion/:id/editar" element={<RequireAdmin><OrderEditPage /></RequireAdmin>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </AuthenticatedTemplate>
    </Suspense>
  );
}
